import type { FC } from 'react'
import { useEffect } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import PolicyListItem from '@/components/policy/PolicyListItem'
import PolicyTile from '@/components/policy/PolicyTile'
import queries from '@/modules/ecom-policy/queries'
import ecomPolicyResources from '@/modules/ecom-policy/resources'

import { useShopNewOrderContext } from '../../ShopNewOrder.context'

import './styles.scss'

const StepPolicies: FC<{
  customerId: number | undefined
}> = ({ customerId: currentCustomerId }) => {
  const newShopOrderCtx = useShopNewOrderContext()
  const [costObjectId] = newShopOrderCtx.costObjectId
  const [, setActiveStepName] = newShopOrderCtx.activeStepName
  const [selectedPolicyId, setSelectedPolicyId] = newShopOrderCtx.selectedPolicyId
  const { resetOrderData } = newShopOrderCtx

  useEffect(() => {
    setActiveStepName('POLICY')
  }, [setActiveStepName])

  const { data, isLoading } = useQuery(
    costObjectId ? queries.costObjectPolicyList(costObjectId) : [''],
    () => {
      if (!costObjectId) throw Error('Cost object id missing!')
      return ecomPolicyResources.getCostObjectPolicyList(costObjectId)
    },
    { enabled: Boolean(costObjectId) }
  )

  if (isLoading) {
    return <LoaderCircle />
  }

  if (!data) return null

  if (!data.length) {
    return (
      <NoResults>
        <Translation id="No available policies" />
      </NoResults>
    )
  }

  const policyOptions = data.filter(
    ({ customerHeadName, customerId, isActive }) =>
      (customerHeadName || customerId === currentCustomerId) && isActive
  )
  const isPolicyTableVisible = policyOptions.length > 12

  function handleSelectPolicy(policyId: number) {
    // When changing the policy, all related selections should be wiped
    resetOrderData(true)
    setSelectedPolicyId(policyId)
  }

  return (
    <div className={`step-policy ${isPolicyTableVisible ? 'table' : ''}`}>
      {isPolicyTableVisible ? (
        <table>
          <thead>
            <tr>
              <th />
              <th>
                <Translation id="name" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="description" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="price list" />
              </th>
              <th className="is-mobile-hidden number">
                <Translation id="covered amount" />
              </th>
            </tr>
          </thead>

          <tbody>
            {policyOptions.map(policy => (
              <PolicyListItem
                key={policy.id}
                selectedPolicyIds={selectedPolicyId ? [selectedPolicyId] : null}
                onPolicySelect={handleSelectPolicy}
                {...policy}
              />
            ))}
          </tbody>
        </table>
      ) : (
        policyOptions.map(policyListItem => (
          <PolicyTile
            key={policyListItem.id}
            selectedPolicyIds={selectedPolicyId ? [selectedPolicyId] : null}
            onPolicySelect={handleSelectPolicy}
            {...policyListItem}
          />
        ))
      )}
    </div>
  )
}

export default StepPolicies
